import { Injector, Injectable } from '@angular/core';
import { Overlay, ScrollStrategy } from '@angular/cdk/overlay';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';

import {
  SgClickConfirmPlugin,
  SgClickConfirmPluginContext,
  SgClickConfirmPlugins,
  SgClickConfirmScrollStrategies,
} from '../../../../types';
import { SgConfirmClickRef } from '../../../../confirm-click-ref';
import { SgClickConfirmDialogContainerContext, SgClickConfirmDialogContainer } from './container.component';

declare module '../../../../types' {
  interface SgClickConfirmPlugins {
    dialog: SgClickConfirmDialogPlugin;
  }
}

@Injectable()
export class SgClickConfirmDialogPlugin implements SgClickConfirmPlugin {
  readonly name = 'dialog' as 'dialog';

  constructor(private overlay: Overlay, private dialog: MatDialog) {}

  open(event: MouseEvent, ctx: SgClickConfirmPluginContext): SgConfirmClickRef {
    const sgConfirmClickRef = SgConfirmClickRef.create(this.name, ctx);
    const injector = Injector.create({
      providers: [{ provide: SgConfirmClickRef, useValue: sgConfirmClickRef }],
      parent: ctx.injector || ctx.vcRef.injector,
    });

    const unsub = sgConfirmClickRef.closed.subscribe((value) => {
      unsub.unsubscribe();
      dialogRef.close(value);
      ctx.vcRef.clear();
      ctx.confirm(value);
    });

    const config: MatDialogConfig<SgClickConfirmDialogContainerContext> = {
      data: { component: ctx.component, injector },
    };
    if (ctx.options) {
      ['backdropClass', 'hasBackdrop', 'autoFocus', 'disableClose'].forEach((k) => {
        if (ctx.options.hasOwnProperty(k)) {
          config[k] = ctx.options[k];
        }
      });

      if (ctx.options.hasOwnProperty('scrollStrategy')) {
        config.scrollStrategy = this.getScrollStrategyInstance(ctx.options.scrollStrategy);
      }
    }
    const dialogRef = this.dialog.open(SgClickConfirmDialogContainer, config);
    return sgConfirmClickRef;
  }

  private getScrollStrategyInstance(strategy: SgClickConfirmScrollStrategies): ScrollStrategy {
    switch (strategy) {
      case 'block':
        return this.overlay.scrollStrategies.block();
      case 'reposition':
        return this.overlay.scrollStrategies.reposition();
      case 'close':
        return this.overlay.scrollStrategies.close();
      case 'noop':
      default:
        return this.overlay.scrollStrategies.noop();
    }
  }
}
